import { apiUrl } from "../utils/api";
import { apiFetch } from "../utils/apiFetch";
import type { CleanupContactInfo } from "./ansprechpartnerCleanupService";

export type NamenszusatzStatus = "open" | "erledigt";

/**
 * Eine Zeile der Worklist „Namenszusätze bereinigen": ein Kontakt mit gefülltem
 * Namenszusatz. Erbt die Stammdaten-Felder von `CleanupContactInfo`, damit die
 * Vorschau (`CleanupContactPreview`) unverändert wiederverwendet werden kann.
 */
export interface NamenszusatzRow extends CleanupContactInfo {
  id: number;
  contactType: string | null;
  lastname: string | null;
  firstName: string | null;
  suffix: string | null;
  kundennummer: string | null;
  statusLabel: string | null;
  verantwortlich: string | null;
  contractCount: number;
  legalFormLabel: string | null;
  reviewedAt: string | null;
  reviewedBy: string | null;
}

/** Die drei bearbeitbaren Namensfelder. */
export interface NamenszusatzSaveInput {
  lastname: string | null;
  firstName: string | null;
  suffix: string | null;
}

export const listNamenszusatzCleanup = async (
  status: NamenszusatzStatus,
  q: string | undefined,
  teamId: string | undefined,
  limit: number,
  offset: number,
  signal?: AbortSignal,
): Promise<{ rows: NamenszusatzRow[]; total: number }> => {
  const { data, total } = await apiFetch<NamenszusatzRow[]>(
    "namenszusatz-cleanup",
    {
      params: {
        status,
        ...(q ? { q } : {}),
        ...(teamId ? { teamId } : {}),
        limit,
        offset,
      },
      signal,
    },
  );
  return { rows: data ?? [], total: total ?? data?.length ?? 0 };
};

export const fetchNamenszusatzCounts = async (
  teamId?: string,
  signal?: AbortSignal,
): Promise<{ open: number; erledigt: number }> => {
  const { data } = await apiFetch<{ open: number; erledigt: number }>(
    "namenszusatz-cleanup/counts",
    { params: teamId ? { teamId } : {}, signal },
  );
  return data ?? { open: 0, erledigt: 0 };
};

/** BO-Teams für den „Verantwortlich"-Filter. */
export const fetchNamenszusatzTeams = async (
  signal?: AbortSignal,
): Promise<Array<{ id: string; name: string | null }>> => {
  const { data } = await apiFetch<Array<{ id: string; name: string | null }>>(
    "namenszusatz-cleanup/teams",
    { signal },
  );
  return data ?? [];
};

/** Eine einzelne Zeile neu laden (nach dem Speichern im Stammdaten-Panel). */
export const fetchNamenszusatzRow = async (
  id: number,
  signal?: AbortSignal,
): Promise<NamenszusatzRow> => {
  const { data } = await apiFetch<NamenszusatzRow>(
    `namenszusatz-cleanup/${id}`,
    { signal },
  );
  return data;
};

/** POST mit JSON-Body — bewusst rohes fetch: apiFetch setzt keinen Content-Type. */
const post = async <T>(path: string, body?: unknown): Promise<T> => {
  const res = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body ?? {}),
  });
  if (!res.ok) {
    let msg = `Speichern fehlgeschlagen: HTTP ${res.status}`;
    try {
      const d = await res.json();
      if (d?.message)
        msg = Array.isArray(d.message) ? d.message.join(", ") : String(d.message);
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return res.json() as Promise<T>;
};

/** Korrigierte Namensfelder speichern + als geprüft markieren. */
export const saveNamenszusatz = (id: number, input: NamenszusatzSaveInput) =>
  post<{ ok: true; reviewedAt: string }>(
    `namenszusatz-cleanup/${id}/save`,
    input,
  );

/** „Passt so" — unverändert übernehmen und als geprüft markieren. */
export const markNamenszusatzPasstSo = (id: number) =>
  post<{ ok: true; reviewedAt: string | null }>(
    `namenszusatz-cleanup/${id}/passt-so`,
  );

/** Prüf-Markierung zurücknehmen (Reiter „Erledigt"). */
export const reopenNamenszusatz = (id: number) =>
  post<{ ok: true; reviewedAt: string | null }>(
    `namenszusatz-cleanup/${id}/reopen`,
  );
